import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  arraysEqual,
  buildLocationStackState,
  sanitizeLocationStack,
  shouldNavigateBackForStackClose,
} from "./locationStateStackLogic";

export default function useLocationStateStack(
  stateKey,
  items,
  { deferSynchronization = false } = {}
) {
  const location = useLocation();
  const navigate = useNavigate();
  const suppressNavigationRef = useRef(false);
  const suppressNextReconciliationRef = useRef(false);
  const previousOpenStackRef = useRef([]);
  const previousLocationKeyRef = useRef(location.key);
  const nextSyncModeRef = useRef(null);

  const orderedItems = useMemo(() => items, [items]);
  const openStack = useMemo(
    () => orderedItems.filter((item) => item.isOpen).map((item) => item.id),
    [orderedItems]
  );
  const locationStack = useMemo(
    () => sanitizeLocationStack(location.state?.[stateKey], orderedItems),
    [location.state, orderedItems, stateKey]
  );
  const locationChanged = previousLocationKeyRef.current !== location.key;

  const requestReplaceSync = useCallback(() => {
    nextSyncModeRef.current = "replace";
  }, []);

  const requestSuppressNavigationSync = useCallback(() => {
    nextSyncModeRef.current = "suppress";
    suppressNextReconciliationRef.current = true;
  }, []);

  useEffect(() => {
    const previousOpenStack = previousOpenStackRef.current;

    if (deferSynchronization) {
      previousOpenStackRef.current = openStack;
      previousLocationKeyRef.current = location.key;
      return;
    }

    if (suppressNavigationRef.current) {
      suppressNavigationRef.current = false;
      nextSyncModeRef.current = null;
      previousOpenStackRef.current = openStack;
      previousLocationKeyRef.current = location.key;
      return;
    }

    if (arraysEqual(openStack, locationStack)) {
      if (locationChanged) {
        suppressNextReconciliationRef.current = false;
      }
      nextSyncModeRef.current = null;
      previousOpenStackRef.current = openStack;
      previousLocationKeyRef.current = location.key;
      return;
    }

    if (locationChanged) {
      nextSyncModeRef.current = null;
      previousOpenStackRef.current = openStack;
      previousLocationKeyRef.current = location.key;
      return;
    }

    const requestedSyncMode = nextSyncModeRef.current;
    const nextState = buildLocationStackState(
      location.state,
      stateKey,
      openStack
    );

    if (requestedSyncMode === "suppress") {
      nextSyncModeRef.current = null;
      previousOpenStackRef.current = openStack;
      previousLocationKeyRef.current = location.key;
      return;
    }

    if (
      shouldNavigateBackForStackClose({
        openStack,
        previousOpenStack,
        locationStack,
        requestedSyncMode,
      })
    ) {
      navigate(-1);
    } else {
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        {
          replace:
            requestedSyncMode === "replace" ||
            openStack.length < previousOpenStack.length,
          state: nextState,
        }
      );
    }

    nextSyncModeRef.current = null;
    previousOpenStackRef.current = openStack;
    previousLocationKeyRef.current = location.key;
  }, [
    location.key,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    locationChanged,
    locationStack,
    navigate,
    openStack,
    stateKey,
    deferSynchronization,
  ]);

  useEffect(() => {
    if (deferSynchronization) {
      return;
    }

    if (!locationChanged) {
      return;
    }

    if (suppressNextReconciliationRef.current) {
      suppressNextReconciliationRef.current = false;
      nextSyncModeRef.current = null;
      previousOpenStackRef.current = openStack;
      previousLocationKeyRef.current = location.key;
      return;
    }

    if (arraysEqual(openStack, locationStack)) {
      return;
    }

    const targetIds = new Set(locationStack);
    const overlaysToClose = [...orderedItems]
      .reverse()
      .filter((item) => item.isOpen && !targetIds.has(item.id));

    if (overlaysToClose.length === 0) {
      nextSyncModeRef.current = null;
      previousLocationKeyRef.current = location.key;
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        {
          replace: true,
          state: buildLocationStackState(location.state, stateKey, openStack),
        }
      );
      return;
    }

    nextSyncModeRef.current = null;
    suppressNavigationRef.current = true;
    previousLocationKeyRef.current = location.key;
    overlaysToClose.forEach((item) => item.close());
  }, [
    location.key,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    locationChanged,
    locationStack,
    navigate,
    openStack,
    orderedItems,
    stateKey,
    deferSynchronization,
  ]);

  return {
    requestReplaceSync,
    requestSuppressNavigationSync,
  };
}
